import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  CheckCircle2, ClipboardCheck, FileDown, Lock, Palette, Power, Users, X,
} from "lucide-react";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import StatTile from "../../components/StatTile";
import {
  getAdminOverview, getAllDesigners, setDesignerActive,
  getPhaseApplications, approvePhaseApplication,
  generatePaymentReceipt, downloadPaymentReceipt,
} from "../../services/interiorDesignerAdmin";

/* ---------- helpers ---------- */
const fmtINR = (n) => {
  const v = Number(n);
  if (n == null || Number.isNaN(v)) return "—";
  return `₹${v.toLocaleString("en-IN")}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);

const PAYMENT_MODES = ["UPI", "Bank Transfer", "Cheque", "Cash", "Other"];

const STATUS_BADGE = {
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
};

const InteriorDesigners = () => {
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState("designers");

  /* designers */
  const [designers, setDesigners] = useState([]);
  const [designersLoading, setDesignersLoading] = useState(true);
  const [confirmToggle, setConfirmToggle] = useState(null); // {id, name, isActive}

  /* applications */
  const [appsFilter, setAppsFilter] = useState("pending"); // pending | all
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [review, setReview] = useState(null); // application being reviewed
  const [decision, setDecision] = useState("PAID");
  const [payment, setPayment] = useState({ date: todayISO(), mode: "UPI", reference: "" });
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyReceiptId, setBusyReceiptId] = useState(null);

  const loadOverview = useCallback(async () => {
    try { setOverview(await getAdminOverview()); } catch (e) { /* hero only */ }
  }, []);
  const loadDesigners = useCallback(async () => {
    setDesignersLoading(true);
    try { setDesigners((await getAllDesigners()) || []); }
    catch (e) { toast.error("Failed to load designers"); }
    finally { setDesignersLoading(false); }
  }, []);
  const loadApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      const items = (await getPhaseApplications(appsFilter === "pending")) || [];
      setApps(items);
      if (appsFilter === "pending") setPendingCount(items.length);
      else setPendingCount(items.filter((a) => a.paymentStatus === "PENDING").length);
    } catch (e) { toast.error("Failed to load applications"); }
    finally { setAppsLoading(false); }
  }, [appsFilter]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadDesigners(); }, [loadDesigners]);
  useEffect(() => { loadApps(); }, [loadApps]);

  /* ---------- designers actions ---------- */
  const doToggleActive = async () => {
    const { id, isActive } = confirmToggle;
    setConfirmToggle(null);
    try {
      await setDesignerActive(id, !isActive);
      toast.success(isActive ? "Designer deactivated" : "Designer activated");
      await Promise.all([loadDesigners(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update designer");
    }
  };

  /* ---------- applications actions ---------- */
  const openReview = (item) => {
    setReview(item);
    setDecision("PAID");
    setPayment({ date: todayISO(), mode: "UPI", reference: "" });
    setComment("");
  };

  const submitReview = async () => {
    const id = review?.interiorDesignerPhaseSubscriptionId;
    if (!id) return;
    if (decision === "PAID" && !payment.date) return toast.error("Set the payment date");
    setSubmitting(true);
    try {
      const body = { paymentStatus: decision, comment: comment || null };
      if (decision === "PAID") {
        body.paymentDate = new Date(`${payment.date}T00:00:00`).toISOString();
        body.paymentMode = payment.mode;
        body.paymentReference = payment.reference || null;
      }
      const res = await approvePhaseApplication(id, body);
      if (decision === "PAID") {
        if (res?.receiptUrl) toast.success("Application approved — signed receipt generated and emailed to the designer");
        else toast.warn("Approved, but the receipt could not be generated — use the Generate button in the list");
      } else {
        toast.success("Application rejected");
      }
      setReview(null);
      await loadApps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit decision");
    } finally { setSubmitting(false); }
  };

  const handleGenerateReceipt = async (item) => {
    const id = item.interiorDesignerPhaseSubscriptionId;
    setBusyReceiptId(id);
    try {
      await generatePaymentReceipt(id);
      toast.success("Signed receipt generated and emailed to the designer");
      await loadApps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate receipt");
    } finally { setBusyReceiptId(null); }
  };

  const handleDownloadReceipt = async (item) => {
    try {
      const res = await downloadPaymentReceipt(item.interiorDesignerPhaseSubscriptionId);
      const url = res?.url;
      if (url) window.open(url, "_blank", "noopener");
      else toast.error("Receipt URL unavailable");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open receipt");
    }
  };

  /* ---------- tables ---------- */
  const designerColumns = [
    { key: "name", header: "Designer", width: 240, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
        <p className="text-xs text-slate-500 truncate">{r.companyName || "Independent"}</p>
      </div>
    )},
    { key: "contact", header: "Contact", width: 220, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm text-slate-700 truncate">{r.email}</p>
        <p className="text-xs text-slate-500">{r.mobile}</p>
      </div>
    )},
    { key: "exp", header: "Experience", width: 160, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm text-slate-700">{r.yearsExperience != null ? `${r.yearsExperience} yrs` : "—"}</p>
        <p className="text-xs text-slate-500 truncate">{r.specialization || ""}</p>
      </div>
    )},
    { key: "cities", header: "Cities", width: 180, render: (r) => (
      <div className="flex flex-wrap gap-1">
        {(r.availableCities || []).slice(0, 3).map((c) => (
          <span key={c} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px]">{c}</span>
        ))}
        {(r.availableCities || []).length > 3 && <span className="text-[11px] text-slate-400">+{r.availableCities.length - 3}</span>}
        {(!r.availableCities || r.availableCities.length === 0) && <span className="text-xs text-slate-400">—</span>}
      </div>
    )},
    { key: "status", header: "Status", width: 170, render: (r) => (
      <div className="flex flex-wrap items-center gap-1">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${r.isActive ? STATUS_BADGE.PAID : STATUS_BADGE.REJECTED}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
        {r.isLocked && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-slate-100 text-slate-600 border-slate-200">
            <Lock size={10} /> LOCKED
          </span>
        )}
      </div>
    )},
    { key: "joined", header: "Joined", width: 120, render: (r) => <span className="text-sm text-slate-600">{fmtDate(r.createdDate)}</span> },
    { key: "actions", header: "Actions", width: 130, align: "center", render: (r) => (
      <button
        onClick={() => setConfirmToggle({ id: r.interiorDesignerId, name: r.name, isActive: r.isActive })}
        className={`inline-flex items-center gap-1.5 rounded-lg border text-xs font-semibold py-1.5 px-3 ${
          r.isActive
            ? "border-amber-200 text-amber-600 hover:bg-amber-50"
            : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
        }`}
      >
        <Power size={13} /> {r.isActive ? "Deactivate" : "Activate"}
      </button>
    )},
  ];

  const appColumns = [
    { key: "designer", header: "Designer", width: 200, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{r.interiorDesigner?.name || "—"}</p>
        <p className="text-xs text-slate-500 truncate">{r.interiorDesigner?.email || ""}</p>
      </div>
    )},
    { key: "project", header: "Project · Phase", width: 220, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm text-slate-700 truncate">{r.projectPhase?.project?.projectName || "—"}</p>
        <p className="text-xs text-slate-500 truncate">{r.projectPhase?.name || r.projectPhase?.phaseName || "—"}</p>
      </div>
    )},
    { key: "fee", header: "Fee", width: 110, render: (r) => <span className="text-sm font-medium text-slate-800 tabular-nums">{fmtINR(r.subscriptionFee)}</span> },
    { key: "requested", header: "Requested", width: 120, render: (r) => <span className="text-sm text-slate-600">{fmtDate(r.createdDate)}</span> },
    { key: "status", header: "Status", width: 120, render: (r) => (
      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${STATUS_BADGE[r.paymentStatus] || STATUS_BADGE.PENDING}`}>{r.paymentStatus}</span>
    )},
    { key: "payment", header: "Payment", width: 170, render: (r) => (
      r.paymentStatus === "PAID" ? (
        <div className="min-w-0">
          <p className="text-sm text-slate-700">{fmtDate(r.paymentDate)}{r.paymentMode ? ` · ${r.paymentMode}` : ""}</p>
          <p className="text-xs text-slate-500 truncate">{r.paymentReference || ""}</p>
        </div>
      ) : <span className="text-xs text-slate-400">—</span>
    )},
    { key: "receipt", header: "Receipt", width: 130, align: "center", render: (r) => {
      if (r.paymentStatus !== "PAID") return <span className="text-xs text-slate-400">—</span>;
      const id = r.interiorDesignerPhaseSubscriptionId;
      return r.receiptUrl ? (
        <button onClick={() => handleDownloadReceipt(r)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-flamingo-200 text-flamingo-600 hover:bg-flamingo-50 text-xs font-semibold py-1.5 px-3">
          <FileDown size={13} /> Download
        </button>
      ) : (
        <button onClick={() => handleGenerateReceipt(r)} disabled={busyReceiptId === id}
          className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-xs font-semibold py-1.5 px-3">
          {busyReceiptId === id ? "Generating…" : "Generate"}
        </button>
      );
    }},
    { key: "action", header: "Action", width: 110, align: "center", render: (r) => (
      r.paymentStatus === "PENDING" ? (
        <button onClick={() => openReview(r)}
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-1.5 px-3">
          Review
        </button>
      ) : <span className="text-xs text-slate-400">—</span>
    )},
  ];

  const TABS = [
    { key: "designers", label: "Designers" },
    { key: "applications", label: "Applications", count: pendingCount > 0 ? pendingCount : undefined },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Palette}
        title="Interior Designers"
        subtitle="Designer accounts, phase applications and payment receipts"
      />

      {/* Hero stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={Users} label="Active IDs" value={overview?.activeInteriorDesigners ?? "—"} subtitle={`of ${overview?.totalInteriorDesigners ?? "—"} total`} accent="emerald" />
        <StatTile icon={Palette} label="Designs Submitted" value={overview?.totalDesignsSubmitted ?? "—"} accent="indigo" />
        <StatTile icon={CheckCircle2} label="Designs Finalized" value={overview?.totalDesignsFinalized ?? "—"} accent="flamingo" />
        <StatTile icon={ClipboardCheck} label="Pending Applications" value={pendingCount} accent="slate" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

        <div className="p-5">
          {activeTab === "designers" && (
            <DataTable
              columns={designerColumns}
              data={designers}
              loading={designersLoading}
              minWidth={1080}
              rowKey={(r, i) => r?.interiorDesignerId ?? i}
              emptyState={
                <div className="px-6 py-12 text-center">
                  <Users size={28} className="mx-auto text-slate-300" />
                  <p className="text-base font-semibold text-slate-900 mt-2">No designers yet</p>
                  <p className="text-sm text-slate-500 mt-1">Interior designers appear here once they sign up.</p>
                </div>
              }
            />
          )}

          {activeTab === "applications" && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                {["pending", "all"].map((f) => (
                  <button key={f} onClick={() => setAppsFilter(f)}
                    className={`rounded-lg text-xs font-semibold py-1.5 px-3 border ${
                      appsFilter === f
                        ? "bg-flamingo-600 border-flamingo-600 text-white"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}>
                    {f === "pending" ? "Pending" : "All applications"}
                  </button>
                ))}
              </div>
              <DataTable
                columns={appColumns}
                data={apps}
                loading={appsLoading}
                minWidth={1180}
                rowKey={(r, i) => r?.interiorDesignerPhaseSubscriptionId ?? i}
                emptyState={
                  <div className="px-6 py-12 text-center">
                    <ClipboardCheck size={28} className="mx-auto text-slate-300" />
                    <p className="text-base font-semibold text-slate-900 mt-2">{appsFilter === "pending" ? "No pending applications" : "No applications yet"}</p>
                    <p className="text-sm text-slate-500 mt-1">Phase applications from designers appear here.</p>
                  </div>
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Review modal */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${review ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">Review Application</h3>
            <button onClick={() => setReview(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
              <div><p className="text-xs text-slate-500">Designer</p><p className="font-semibold text-slate-900">{review?.interiorDesigner?.name}</p></div>
              <div><p className="text-xs text-slate-500">Fee</p><p className="font-semibold text-slate-900">{fmtINR(review?.subscriptionFee)}</p></div>
              <div><p className="text-xs text-slate-500">Project</p><p className="text-slate-700">{review?.projectPhase?.project?.projectName || "—"}</p></div>
              <div><p className="text-xs text-slate-500">Phase</p><p className="text-slate-700">{review?.projectPhase?.name || "—"}</p></div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Decision</label>
              <div className="mt-1.5 flex gap-2">
                <button onClick={() => setDecision("PAID")}
                  className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${decision === "PAID" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  Approve (Paid)
                </button>
                <button onClick={() => setDecision("REJECTED")}
                  className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold ${decision === "REJECTED" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  Reject
                </button>
              </div>
            </div>

            {decision === "PAID" && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Payment Info</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Payment Date <span className="text-flamingo-600">*</span></label>
                    <input type="date" value={payment.date} max={todayISO()}
                      onChange={(e) => setPayment({ ...payment, date: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Payment Mode</label>
                    <select value={payment.mode} onChange={(e) => setPayment({ ...payment, mode: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500">
                      {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-slate-600">Reference / UTR No.</label>
                    <input value={payment.reference} placeholder="(optional)"
                      onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500" />
                  </div>
                </div>
                <p className="text-[11px] text-emerald-700">Approving generates a digitally-signed payment receipt and emails it to the designer.</p>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-slate-600">Comment</label>
              <textarea rows={2} value={comment} placeholder="(optional)"
                onChange={(e) => setComment(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500" />
            </div>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setReview(null)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={submitReview} disabled={submitting}
              className={`rounded-xl disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 ${decision === "PAID" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
              {submitting ? "Submitting…" : decision === "PAID" ? "Approve & Generate Receipt" : "Reject Application"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirmToggle}
        onConfirm={doToggleActive}
        onCancel={() => setConfirmToggle(null)}
        message={
          confirmToggle?.isActive
            ? `Deactivate "${confirmToggle?.name}"? They will lose access until reactivated.`
            : `Activate "${confirmToggle?.name}"?`
        }
      />
    </div>
  );
};

export default InteriorDesigners;
